import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'

/**
 * Export multiple sheets to a single .xlsx file.
 * @param {Array<{name: string, data: Array<Array>}>} sheets - Each sheet has a name and 2D array (first row = headers)
 * @param {string} filename - Output filename (without .xlsx extension)
 */
export function exportMultiSheet(sheets, filename) {
    const wb = XLSX.utils.book_new()

    sheets.forEach(sheet => {
        if (!sheet || !sheet.data || sheet.data.length === 0) return

        const ws = XLSX.utils.aoa_to_sheet(sheet.data)

        // Auto-size columns based on content width
        const colCount = sheet.data[0].length
        ws['!cols'] = Array.from({ length: colCount }, (_, i) => {
            const maxLen = sheet.data.reduce((max, row) => {
                const val = row[i]
                const len = val != null ? String(val).length : 0
                return Math.max(max, len)
            }, 0)
            return { wch: Math.min(Math.max(maxLen + 2, 10), 40) }
        })

        XLSX.utils.book_append_sheet(wb, ws, sheet.name.slice(0, 31))
    })

    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${filename}.xlsx`)
}

/**
 * Build sheet for Daily Production data
 */
export function buildDailyProductionSheet(production, isNet, prefix) {
    if (!production?.actual?.length) return null

    const headers = ['Date', `${prefix} Oil (BBL)`, `${prefix} Gas (MCF)`, 'Water (BBL)']
    const rows = production.actual.map(d => [
        d.date,
        isNet ? (d.net_oil_bbl ?? '') : (d.oil_bbl ?? ''),
        isNet ? (d.net_gas_mcf ?? '') : (d.gas_mcf ?? ''),
        d.water_bbl ?? ''
    ])

    return { name: 'Daily Production', data: [headers, ...rows] }
}

/**
 * Build sheet for WTI Price history
 */
export function buildWTISheet(wtiPrice) {
    if (!wtiPrice?.history?.length) return null

    const headers = ['Date', 'WTI Price ($/BBL)']
    const rows = wtiPrice.history.map(d => [
        d.date,
        d.price != null ? Math.round(d.price * 100) / 100 : ''
    ])

    return { name: 'WTI Price', data: [headers, ...rows] }
}

/**
 * Build sheet for Production Concentration (Pareto)
 */
export function buildConcentrationSheet(concentration) {
    if (!concentration?.length) return null

    // Calculate cumulative % if not already present
    const totalOil = concentration.reduce((sum, w) => sum + (w.oil || 0), 0)
    let cumOil = 0

    const headers = ['Well', 'Oil (BBL)', '% of Total', 'Cumulative %']
    const rows = concentration.map(w => {
        cumOil += (w.oil || 0)
        const pct = totalOil > 0 ? Math.round((w.oil / totalOil) * 10000) / 100 : 0
        const cumPct = totalOil > 0 ? Math.round((cumOil / totalOil) * 10000) / 100 : 0
        return [w.name || w.wellName || '', w.oil ?? '', pct, cumPct]
    })

    return { name: 'Production Concentration', data: [headers, ...rows] }
}

/**
 * Build sheet for Top Producing Wells
 */
export function buildTopWellsSheet(wells, isNet, prefix) {
    if (!wells?.length) return null

    const headers = ['Well', `${prefix} Oil (BBL/d)`, `${prefix} Gas (MCF/d)`, 'Water (BBL/d)', 'Uptime %']
    const rows = wells.map(w => [
        w.wellName || w.name || '',
        isNet ? (w.netOilPerDay ?? '') : (w.grossOilPerDay ?? w.oilPerDay ?? ''),
        isNet ? (w.netGasPerDay ?? '') : (w.grossGasPerDay ?? w.gasPerDay ?? ''),
        w.waterPerDay ?? '',
        w.uptimePercent != null ? Math.round(w.uptimePercent * 10) / 10 : ''
    ])

    return { name: 'Top Wells', data: [headers, ...rows] }
}

/**
 * Build sheet for Monthly Production data (actuals only — no forecast)
 */
export function buildMonthlySheet(monthlyData, prefix, isNet) {
    if (!monthlyData?.monthly?.length) return null

    const oilActKey = isNet ? 'netOilActual' : 'grossOilActual'
    const gasActKey = isNet ? 'netGasActual' : 'grossGasActual'

    const headers = [
        'Month',
        `${prefix} Oil Actual (BBL)`,
        `${prefix} Gas Actual (MCF)`
    ]

    const rows = monthlyData.monthly.map(m => [
        m.label,
        m[oilActKey] ?? '',
        m[gasActKey] ?? ''
    ])

    return { name: 'Monthly Production', data: [headers, ...rows] }
}

/**
 * Build sheet for Quarterly Production data (actuals only — no forecast)
 */
export function buildQuarterlySheet(monthlyData, prefix, isNet) {
    if (!monthlyData?.quarterly?.length) return null

    const oilActKey = isNet ? 'netOilActual' : 'grossOilActual'

    const headers = ['Quarter', `${prefix} Oil Actual (BBL)`]
    const rows = monthlyData.quarterly.map(q => [
        q.label,
        q[oilActKey] ?? ''
    ])

    return { name: 'Quarterly Summary', data: [headers, ...rows] }
}
